/**
 * Invoices Page - Tailwind Version
 *
 * Professional Uber Eats Style Invoice Manager.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Storage } from "aws-amplify";
import moment from "moment";
import { Calendar, Download, FileText, RefreshCw, Info, ChevronRight, FileJson } from "lucide-react";
import { useRestaurantContext } from "../../context/restaurant-context";
import { useInvoiceContext } from "../../context/invoice-context";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { cn, formatPrice } from "../../lib/utils";
import { toast } from "sonner";
import { RefundSummary } from "../../components/refund-summary";

interface ParsedInvoice {
  key: string;
  filename: string;
  startDate: string;
  endDate: string;
  isJson: boolean;
}

export default function InvoicesPage() {
  const { restaurant } = useRestaurantContext();
  const invoiceContext = useInvoiceContext();
  const [activeTab, setActiveTab] = useState("current");
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<ParsedInvoice[]>([]);

  const parseInvoiceKey = (key: string): ParsedInvoice | null => {
    const parts = key.split('/');
    const filename = parts[parts.length - 1];
    const isJson = filename.endsWith('.json');
    
    // Pattern: 01.01.2025-13.04.2025.json or similar
    const dateMatch = filename.match(/(\d{2}\.\d{2}\.\d{4})-(\d{2}\.\d{2}\.\d{4})/);
    
    if (dateMatch) {
      return {
        key,
        filename,
        startDate: dateMatch[1],
        endDate: dateMatch[2],
        isJson
      };
    }
    return null;
  };

  const fetchData = useCallback(async () => {
    if (!restaurant?.id) return;
    setLoading(true);
    try {
      const res = await Storage.list(`invoices/${restaurant.id}/`, { level: "public" });
      const parsed = res.results
        .map(i => i.key ? parseInvoiceKey(i.key) : null)
        .filter((i): i is ParsedInvoice => i !== null)
        .sort((a, b) => {
          // Sort by start date descending
          const dateA = moment(a.startDate, "DD.MM.YYYY").toDate();
          const dateB = moment(b.startDate, "DD.MM.YYYY").toDate();
          return dateB.getTime() - dateA.getTime();
        });
      setInvoices(parsed);
    } catch (error) {
      toast.error("Fehler beim Laden der Abrechnungen");
    } finally {
      setLoading(false);
    }
  }, [restaurant?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleViewInvoice = async (invoice: ParsedInvoice) => {
    if (!invoice.isJson) {
      // For PDFs, we just get the URL and open
      const url = await Storage.get(invoice.key, { level: "public" });
      window.open(String(url), '_blank');
      return;
    }

    // For JSON, we use the existing context logic
    try {
      // Fix: Storage.get returns string or object depending on options
      const res: any = await Storage.get(invoice.key, {
        level: "public",
        download: true,
      });
      const jsonText = await res.Body.text();
      const data = JSON.parse(jsonText);
      
      if (invoiceContext.onSetupInvoiceModal) {
        invoiceContext.onSetupInvoiceModal(data, `${invoice.startDate} - ${invoice.endDate}`);
      }
    } catch (e) {
      toast.error("Abrechnung konnte nicht geladen werden");
    }
  };

  return (
    <div className="w-full space-y-10 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b pb-4">
        <div className="space-y-6">
          <h1 className="text-3xl font-black tracking-tight">Payments</h1>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
            <TabsList className="bg-transparent p-0 h-auto gap-10 border-none">
              <TabsTrigger value="current" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent px-0 pb-3 h-auto font-bold text-sm text-muted-foreground data-[state=active]:text-foreground transition-all uppercase text-[10px] tracking-widest">Invoices</TabsTrigger>
              <TabsTrigger value="refunds" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent px-0 pb-3 h-auto font-bold text-sm text-muted-foreground data-[state=active]:text-foreground transition-all uppercase text-[10px] tracking-widest">Refunds</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="h-9 px-4 rounded-lg font-bold border-muted-foreground/20">
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} /> Aktualisieren
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} className="w-full">
        <TabsContent value="current" className="mt-0 space-y-4">
          <div className="bg-primary/5 p-4 rounded-2xl border border-primary/10 flex items-start gap-3 mb-6">
            <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <p className="text-xs font-medium leading-relaxed">
              Hier findest du deine wöchentlichen Abrechnungen. JSON-Dateien können direkt im Browser angezeigt werden, PDFs werden heruntergeladen.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-px border rounded-2xl overflow-hidden bg-muted-foreground/10 border-muted-foreground/10 shadow-sm">
            {invoices.length === 0 && !loading ? (
              <div className="bg-background p-12 text-center">
                <FileText className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground font-bold">Keine Abrechnungen gefunden.</p>
              </div>
            ) : (
              invoices.map((inv) => (
                <div 
                  key={inv.key} 
                  className="group flex items-center justify-between p-4 bg-background hover:bg-muted/5 transition-all cursor-pointer border-b last:border-b-0 border-muted-foreground/5"
                  onClick={() => handleViewInvoice(inv)}
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="h-10 w-10 rounded-xl bg-muted/20 flex items-center justify-center shrink-0 border border-muted-foreground/5">
                      {inv.isJson ? <FileJson className="h-5 w-5 text-primary/60" /> : <FileText className="h-5 w-5 text-muted-foreground/40" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="font-bold text-sm block leading-tight">Wochenabrechnung</span>
                      <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest mt-1">
                        {inv.startDate} — {inv.endDate}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 ml-4">
                    <Button variant="ghost" size="sm" className="font-bold text-[10px] uppercase tracking-widest hover:bg-primary/5 hover:text-primary">Anzeigen</Button>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="refunds" className="mt-0">
          <RefundSummary cases={[]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
